// Asisten AI (plan C2), the landing screen for guests and users.
// Routes: "/" is a new conversation, "/asisten/:id" opens one of the user's
// own conversations (a new conversation moves there after its first answer).
// A guest (no session) chats in memory only through POST /chat/guest.
//
// Regions (design review ui.region_gate; the old toolbar with Riwayat and
// Bantuan is gone: Riwayat lives in the sidebar, Bantuan in the profile menu):
//   empty_title    plain spacing   one centred h1, "Mau tanya apa hari ini?"
//   composer       control         large and centred on the empty state,
//                                  docked at the bottom after the first message
//   faq_chips      chip row        saved questions under the composer (empty
//                                  state, relevance 1.86); in a thread they sit
//                                  behind the composer's Pertanyaan umum button
//   pbj_prompts    cards           a Non-Penyedia's Chat baru only, under the
//                                  chips: Perencanaan Pengadaan and HPS ask at
//                                  once, Alur PBJ and Template open their menu
//   guest_quota    plain spacing   "Sisa N dari 5 pesan sebelum jeda" (relevance
//                                  2.76); at zero the composer locks and the
//                                  notice offers Lanjutkan dengan Google
//   thread         plain spacing   questions and answers (core.ai_chat), the
//                                  Usulan dokumen card (chat/Proposal.tsx)
// (The ai_reminder line under the composer left in round 2, at the user's
// request, with no replacement.)
// In a thread the screen's one h1 is the conversation title, one quiet line
// above the thread.
// Phase 2: the composer takes files (chat/attachments.ts); a signed-in user's
// files upload at once and travel as attachmentIds, a guest's one file goes
// inline with the guest message. Inside a project (projectId) a new
// conversation and its uploads belong to the project, the empty heading is
// the project's name, and the first answer moves to /project/:id/c/:conv.
// A project home passes `dock`, an element pinned to the bottom of its
// scroller: the empty state's composer renders there (a portal), so it stays
// in sight while the page above it scrolls; the chips stay in the page.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import * as API from "../api";
import type { AttachmentRef, CatalogTahap, ChatMessage, ChatTurnInput, ConversationDetail, FaqItem, KnowledgeFile } from "../api";
import { useUser } from "../auth";
import { isActiveAccount } from "../lib/account";
import { FILES_ONLY_MESSAGE, useAttachments } from "../chat/attachments";
import { Composer } from "../chat/Composer";
import { SuggestionChips } from "../chat/SuggestionChips";
import { PlutoSay, greetingFor, rememberGreeting } from "../chat/PlutoSay";
import { isBareIntakePrefill, type SuggestionChip } from "../project/stage-chips";
import {
  type GuestConversation,
  type GuestTurn,
  guestNow,
  newGuestConversationId,
  refresh as refreshGuest,
  saveGuestConversation,
  setGuestCooldown,
  startGuestPurge,
  useGuestState,
} from "../chat/guest-store";
import type { CatalogState } from "../chat/Proposal";
import { messageKind, type CreateDocumentResult, type ThreadMessage } from "../chat/documents";
import { draftExampleFor, faqGlyph, shortenTitle, useMedia } from "../chat/support";
import { Thread, type Pending } from "../chat/Thread";
import { loadCatalog } from "../lib/catalog";
import { canGenerate, isNonPenyedia } from "../lib/labels";
import { HomePrompts } from "../pbj/HomePrompts";
import { navigate, useLocation } from "../router";
import { useChatShell } from "../shell/chat-context";
import { GoogleButton } from "../shell/GoogleSignIn";
import { Dialog, EmptyState, Notice, useAsync, useDelayed, useTitle, type Async } from "../ui/components";
import { Glyph } from "../ui/icons";
import { formatMinutes } from "../lib/format";

/** The empty state's one heading. */
export const CHAT_GREETING = "Mau tanya apa hari ini?";
/** Chat baru of a Penyedia (Simulasi Pluto v4 supplierHome). */
export const PENYEDIA_GREETING = "Bantu persiapan penawaran Anda";
/** The line under the conversation title in the topbar. */
export const CHAT_TOPBAR_SUB = "Asisten Pengadaan Barang/Jasa Pemerintah";
export const PENYEDIA_GREETING_LEAD = "Cari peluang, buat project, review dokumen pemilihan dan KAK, siapkan penawaran, lalu kendalikan kontrak hingga serah terima.";
/** The composer hint; the heading already asks the question. */
const ASK_PLACEHOLDER = "Tanya seputar pengadaan";
/** Below 640 the paperclip, Pertanyaan umum and Kirim share the row: a hint that fits. */
const ASK_PLACEHOLDER_SHORT = "Tanya Pluto";
const PHONE = "(max-width: 639.98px)";
/** The guest quota per day (D1); the API answers with its own `limit`. */
export const GUEST_LIMIT = 5;
/** How many chips the empty state shows (the drafting example counts as one). */
const CHIP_MAX = 4;
/** The guest history sent with each turn (API max 10). */
const GUEST_HISTORY = 10;

// Knowledge titles for the cited ids, fetched once per page load when an
// answer first cites something.
let knowledgeTitles: Promise<KnowledgeFile[] | null> | null = null;
/** Forgets the cached knowledge titles (tests, and a sign-out). */
export function resetKnowledgeTitles(): void {
  knowledgeTitles = null;
}

function loadKnowledgeTitles(): Promise<KnowledgeFile[] | null> {
  knowledgeTitles ??= API.getKnowledge().then(
    (k) => k.files,
    () => {
      knowledgeTitles = null;
      return null;
    },
  );
  return knowledgeTitles;
}

/** Appends a turn; a reused stored question (contract 8.5 retry) replaces its earlier copy. */
function withTurn(c: ConversationDetail, added: ChatMessage[]): ConversationDetail {
  const ids = new Set(added.map((m) => m.id));
  const messages = [...c.messages.filter((m) => !ids.has(m.id)), ...added];
  return { ...c, messages, messageCount: messages.length, lastMessageAt: added[added.length - 1]?.createdAt ?? c.lastMessageAt };
}

/** Keeps the scroller on the new question (an answer arrived) or at the end. */
function useThreadScroll() {
  const scroller = useRef<HTMLDivElement>(null);
  const mode = useRef<"bottom" | "question" | null>(null);
  useLayoutEffect(() => {
    const el = scroller.current;
    const m = mode.current;
    if (!el || !m) return;
    mode.current = null;
    if (m === "bottom") {
      el.scrollTop = el.scrollHeight;
      return;
    }
    const questions = el.querySelectorAll<HTMLElement>('.msg[data-role="user"]');
    const last = questions[questions.length - 1];
    if (!last) return;
    const top = last.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
    el.scrollTop = Math.max(0, top - 16);
  });
  return { scroller, mode };
}

/** Focuses the composer at the end of its text (after a chip fills it). */
function focusComposer(): void {
  requestAnimationFrame(() => {
    const box = document.querySelector<HTMLTextAreaElement>(".composer textarea");
    box?.focus();
    box?.setSelectionRange?.(box.value.length, box.value.length);
  });
}

export interface ChatScreenProps {
  conversationId?: string;
  guest?: boolean;
  /** The chat inside a project: new conversations and uploads belong to it. */
  projectId?: string;
  /** The project's name, the empty state's heading. */
  projectName?: string;
  /** Overrides the empty state's heading (a Penyedia paket page names Pluto's role instead). */
  greeting?: string;
  /** The empty heading's level; h2 under a page that already has its h1. */
  greetingLevel?: "h1" | "h2";
  /**
   * Text put into the composer (Buat dengan Pluto on a checklist item); a new
   * `seq` fills it again even with the same text.
   */
  prefill?: { text: string; seq: number } | null;
  /** Shown under the composer on the empty state instead of the saved questions (the paket intake chips). */
  intro?: ReactNode;
  /** Stage chips (a Penyedia paket): replace the saved-question chips, also under the composer in a thread. */
  suggestions?: SuggestionChip[];
  /**
   * Pluto's greeting on a project home (no model call, never stored): a
   * bubble with its quick replies (default: the saved questions). It stays
   * above the first conversation it opens, for this page session.
   */
  greetingMessage?: { text: string; chips?: ReactNode; keepHeading?: boolean };
  /** The drafting chip's example inside a project (its own name); default: the role's example. */
  draftExample?: string;
  /** A project home's pinned dock: the empty state's composer renders there (see the header). */
  dock?: HTMLElement | null;
}

export function ChatScreen({ conversationId, guest = false, projectId, projectName, greeting, greetingLevel, prefill, intro, suggestions, greetingMessage, draftExample, dock }: ChatScreenProps) {
  if (guest) return <GuestChat />;
  return (
    <UserChat
      key={projectId ?? ""}
      conversationId={conversationId}
      projectId={projectId}
      projectName={projectName}
      greeting={greeting}
      greetingLevel={greetingLevel}
      prefill={prefill ?? null}
      intro={intro}
      suggestions={suggestions}
      greetingMessage={greetingMessage}
      draftExample={draftExample}
      dock={dock}
    />
  );
}

/** Where a conversation lives: under its project, or in the main chat. */
export function conversationPath(conversationId: string, projectId?: string): string {
  const conv = encodeURIComponent(conversationId);
  return projectId ? `/project/${encodeURIComponent(projectId)}/c/${conv}` : `/asisten/${conv}`;
}

// ---------------------------------------------------------------------------
// The frame both modes share
// ---------------------------------------------------------------------------
interface FrameProps {
  started: boolean;
  title: string;
  scroller: RefObject<HTMLDivElement | null>;
  /** The thread (or its loading and error states). */
  thread: ReactNode;
  composer: ReactNode;
  /** Under the composer on the empty state only. */
  chips?: ReactNode;
  /** The guest quota line or lock notice. */
  quota?: ReactNode;
  /** The empty state's heading (the project's name inside a project). */
  greeting?: string;
  /** h2 when the page already has its h1 (a Penyedia paket page). */
  greetingLevel?: "h1" | "h2";
  /** A line under the greeting, or beside the title in a thread (the guest's role and Ubah peran). */
  aside?: ReactNode;
  /** The line under the conversation title in the topbar. */
  sub?: string;
  /** Pluto's greeting bubble on the empty state (a project home); the heading is then for screen readers only unless kept. */
  hello?: ReactNode;
  /** Keep the heading visible above the greeting (a folder project's name). */
  keepHeading?: boolean;
  /** Where the empty state's composer and quota render instead (a project home's pinned dock). */
  dock?: HTMLElement | null;
}

function ChatFrame({ started, title, scroller, thread, composer, chips, quota, greeting = CHAT_GREETING, greetingLevel = "h1", aside, sub = CHAT_TOPBAR_SUB, hello, keepHeading = false, dock = null }: FrameProps) {
  if (!started) {
    const Heading = greetingLevel;
    return (
      <div className="chat" data-state="empty">
        <section className="chat-main" aria-labelledby="chat-greeting">
          <div className="chat-scroll" ref={scroller} data-ui-scroller="">
            <div className="chat-inner chat-start">
              <Heading id="chat-greeting" className={hello && !keepHeading ? "chat-greeting visually-hidden" : "chat-greeting"}>
                {greeting}
              </Heading>
              {aside ? <div className="chat-aside">{aside}</div> : null}
              {hello ? <ol className="thread chat-hello" aria-label="Sapaan Pluto">{hello}</ol> : null}
              {dock ? (
                <>
                  {chips ? <div className="chat-start-dock">{chips}</div> : null}
                  {createPortal(
                    <div className="chat-inner chat-start-dock">
                      {composer}
                      {quota}
                    </div>,
                    dock,
                  )}
                </>
              ) : (
                <div className="chat-start-dock">
                  {composer}
                  {chips}
                  {quota}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }
  return (
    <div className="chat" data-state="thread">
      <section className="chat-main" aria-labelledby="chat-title">
        {/* Topbar (Simulasi Pluto v4 .topbar): the title and what Pluto is on
            the start; the end is the actions slot, for now the guest's role line. */}
        <header className="chat-head chat-topbar">
          <div className="chat-topbar-text">
            <h1 id="chat-title" className="chat-title" title={title}>
              {title}
            </h1>
            <p className="chat-topbar-sub" title={sub}>{sub}</p>
          </div>
          <div className="chat-topbar-actions">{aside ? <div className="chat-aside">{aside}</div> : null}</div>
        </header>
        <div className="chat-scroll" ref={scroller} data-ui-scroller="">
          <div className="chat-inner chat-thread">{thread}</div>
        </div>
        <div className="chat-dock">
          <div className="chat-inner">
            {composer}
            {quota}
          </div>
        </div>
      </section>
    </div>
  );
}

/** The drafting chip: its example (the role's, or the project's own) and what a click does. */
interface DraftChip {
  example: string;
  onPick: () => void;
}

function FaqChips({ items, disabled, onAsk, draft }: { items: FaqItem[]; disabled: boolean; onAsk: (item: FaqItem) => void; draft?: DraftChip }) {
  if (items.length === 0 && !draft) return null;
  return (
    <div className="chat-chips" role="group" aria-label="Pertanyaan umum">
      {draft ? (
        <button type="button" className="chip chat-chip chat-suggest" title={`Contoh: ${draft.example}`} disabled={disabled} onClick={draft.onPick}>
          <Glyph name="docAdd" size={16} />
          <span className="chip-text">{draft.example}</span>
        </button>
      ) : null}
      {items.slice(0, draft ? CHIP_MAX - 1 : CHIP_MAX).map((item) => (
        <button key={item.id} type="button" className="chip chat-chip" title={item.question} disabled={disabled} onClick={() => onAsk(item)}>
          <Glyph name={faqGlyph(item.icon)} size={16} />
          <span className="chip-text">{item.question}</span>
        </button>
      ))}
    </div>
  );
}

function FaqButton({ open, disabled, onOpen }: { open: boolean; disabled: boolean; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="ui-icon-btn composer-faq"
      data-variant="outlined"
      aria-label="Pertanyaan umum"
      title="Pertanyaan umum"
      aria-haspopup="dialog"
      aria-expanded={open}
      disabled={disabled}
      onClick={onOpen}
    >
      <Glyph name="messages" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Signed-in chat: stored conversations, the document proposal flow
// ---------------------------------------------------------------------------
interface UserChatProps {
  conversationId?: string;
  projectId?: string;
  projectName?: string;
  greeting?: string;
  greetingLevel?: "h1" | "h2";
  prefill: { text: string; seq: number } | null;
  intro?: ReactNode;
  suggestions?: SuggestionChip[];
  greetingMessage?: { text: string; chips?: ReactNode; keepHeading?: boolean };
  draftExample?: string;
  dock?: HTMLElement | null;
}

function UserChat({ conversationId, projectId, projectName, greeting, greetingLevel, prefill, intro, suggestions, greetingMessage, draftExample, dock }: UserChatProps) {
  const user = useUser();
  const shell = useChatShell();
  const bumpHistory = shell?.bumpHistory;
  const renamed = shell?.renamed ?? null;
  const [conv, setConv] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [text, setText] = useState("");
  const [faqOpen, setFaqOpen] = useState(false);
  const [knowledge, setKnowledge] = useState<KnowledgeFile[] | null>(null);
  const { scroller, mode: scrollMode } = useThreadScroll();
  const faq = useAsync(() => API.listFaq(), []);
  const active = isActiveAccount(user);
  const canDraft = canGenerate(user.role) && active;
  const [catalog, setCatalog] = useState<CatalogTahap[] | null>(null);
  const [catalogError, setCatalogError] = useState<unknown>(null);
  const [catalogTry, setCatalogTry] = useState(0);
  const attachments = useAttachments({ projectId });
  const phone = useMedia(PHONE);

  const title = conv?.title.trim() || "Chat";
  // Embedded under a page with its own h1 (a paket page), the page keeps its name as the title.
  useTitle(conv?.title ?? (greetingLevel === "h2" && projectName ? projectName : "Chat"));

  // Buat dengan Pluto: the tailored prompt lands in the composer for the user to adjust and send.
  useEffect(() => {
    if (!prefill) return;
    setText(prefill.text);
    focusComposer();
  }, [prefill?.seq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Follow the route: "/" starts fresh, "/asisten/:id" loads that conversation
  // unless it is the one this screen just created.
  useEffect(() => {
    if (!conversationId) {
      setConv(null);
      setPending(null);
      setLoadError(null);
      setLoading(false);
      return;
    }
    if (conv?.id === conversationId) return;
    let alive = true;
    setLoading(true);
    setLoadError(null);
    setPending(null);
    API.getConversation(conversationId).then(
      (c) => {
        if (!alive) return;
        // A paket proposal card is tall: open at its question so the card starts in view.
        scrollMode.current = c.messages[c.messages.length - 1]?.paketProposal ? "question" : "bottom";
        setConv(c);
        setLoading(false);
      },
      (err) => {
        if (!alive) return;
        setConv(null);
        setLoadError(err);
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
    // conv is read to skip a refetch, never to trigger one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // A rename from the sidebar reaches the open conversation's title.
  useEffect(() => {
    if (renamed) setConv((c) => (c && c.id === renamed.id ? { ...c, title: renamed.title } : c));
  }, [renamed]);

  const messages: ThreadMessage[] = conv?.messages ?? [];
  const cites = messages.some((m) => m.citations.length > 0);

  // The catalog turns a proposal's ids into labels and its missing fields
  // into inputs; it loads only once a proposal is on screen.
  const proposes = canDraft && messages.some((m) => messageKind(m) === "document_proposal");
  useEffect(() => {
    if (!proposes || catalog) return;
    let alive = true;
    setCatalogError(null);
    loadCatalog(user.id).then(
      (c) => alive && setCatalog(c),
      (err) => alive && setCatalogError(err),
    );
    return () => {
      alive = false;
    };
  }, [proposes, catalog, user.id, catalogTry]);
  const catalogState: CatalogState = { data: catalog, error: catalogError, reload: () => setCatalogTry((n) => n + 1) };

  // Buat dokumen succeeded: the proposal is linked to its document and the
  // assistant's confirmation joins the thread.
  const onDocumentCreated = (messageId: string, result: CreateDocumentResult) => {
    setConv((c) => {
      if (!c) return c;
      const linked = { ...c, messages: c.messages.map((m) => (m.id === messageId ? ({ ...m, documentId: result.document.id } as ChatMessage) : m)) };
      return result.message ? withTurn(linked, [result.message as ChatMessage]) : linked;
    });
    bumpHistory?.();
  };
  useEffect(() => {
    if (!cites || knowledge) return;
    let alive = true;
    loadKnowledgeTitles().then((files) => alive && setKnowledge(files));
    return () => {
      alive = false;
    };
  }, [cites, knowledge]);

  const send = useCallback(
    async (input: ChatTurnInput, shown: string, bubble = true, files: AttachmentRef[] = []) => {
      if (pending?.status === "sending") return;
      scrollMode.current = "bottom";
      setPending({ status: "sending", input, text: shown, bubble, files });
      try {
        if (!conv) {
          const created = await API.createConversation(projectId ? { ...input, projectId } : input);
          if (greetingMessage) rememberGreeting(created.id, greetingMessage.text);
          scrollMode.current = "question";
          setConv(created);
          setPending(null);
          navigate(conversationPath(created.id, projectId), { replace: true });
        } else {
          const added = await API.sendTurn(conv.id, input);
          scrollMode.current = "question";
          setConv((c) => (c ? withTurn(c, added) : c));
          setPending(null);
        }
        bumpHistory?.();
      } catch (err) {
        const error = err instanceof API.ApiError ? err : new API.ApiError(0, API.errorMessage(err));
        scrollMode.current = "bottom";
        if (!conv && error.conversationId) {
          // A first question that failed at the AI is stored with its
          // conversation (contract 8.5): open it, so Coba lagi answers there.
          const at = new Date().toISOString();
          setConv({ id: error.conversationId, title: shortenTitle(shown), lastMessageAt: at, messageCount: 1, messages: [], memory: { summary: "", coversMessages: 0, updatedAt: null } });
          navigate(conversationPath(error.conversationId, projectId), { replace: true });
          bumpHistory?.();
        }
        setPending({ status: "error", input, text: shown, bubble, error, files });
      }
    },
    [conv, pending, bumpHistory, scrollMode, projectId, greetingMessage],
  );

  const askFaq = (item: FaqItem) => {
    setFaqOpen(false);
    void send({ faqId: item.id }, item.question);
  };
  const hasSuggestions = (suggestions?.length ?? 0) > 0;
  const [hint, setHint] = useState<string | null>(null);
  // A stage chip goes into the composer for the user to adjust.
  const pickSuggestion = (chip: SuggestionChip) => {
    setText(chip.prompt);
    focusComposer();
  };
  // The drafting example (the project's own, or the role's) goes into the composer for the user to adjust.
  const example = draftExample?.trim() || draftExampleFor(user.role);
  const draftChip: DraftChip | undefined = canDraft
    ? {
        example,
        onPick: () => {
          setText(example);
          focusComposer();
        },
      }
    : undefined;
  // Chat baru of a Non-Penyedia or a Penyedia: the starting points of the
  // role's side (pbj/HomePrompts) under the saved questions; a prompt card asks at once.
  const homePrompts = !projectId && (isNonPenyedia(user.role) || user.role === "penyedia");
  const askPrompt = (question: string) => {
    setHint(null);
    void send({ message: question }, question);
  };
  const askText = (typed: string) => {
    const files = attachments.ready.map((it) => it.ref).filter((r): r is AttachmentRef => !!r);
    // Only the intake chip's opening line, nothing pasted: ask for the text instead of sending it.
    if (files.length === 0 && isBareIntakePrefill(typed)) {
      setHint("Tempel teks pengumumannya di bawah baris pertama (atau ceritakan paketnya), lalu kirim.");
      focusComposer();
      return;
    }
    setHint(null);
    const message = typed || FILES_ONLY_MESSAGE;
    setText("");
    attachments.clear();
    void send(files.length > 0 ? { message, attachmentIds: files.map((f) => f.id) } : { message }, message, true, files);
  };
  const retry = () => {
    if (pending?.status === "error") void send(pending.input, pending.text, pending.bubble, pending.files);
  };
  // A stored question with no answer (the AI failed earlier): the same text
  // again reuses it on the server instead of storing it twice.
  const last = conv?.messages[conv.messages.length - 1];
  const unanswered = !pending && last?.role === "user" && last.source === "user" ? last : null;
  const answerAgain = () => {
    if (!unanswered) return;
    const ids = (unanswered.attachments ?? []).map((a) => a.id);
    void send(ids.length > 0 ? { message: unanswered.content, attachmentIds: ids } : { message: unanswered.content }, unanswered.content, false);
  };

  const busy = pending?.status === "sending";
  const faqItems = faq.data ?? [];
  const started = conv !== null || pending !== null || loading || loadError !== null;

  let thread: ReactNode = null;
  if (loading) thread = <ConversationLoading />;
  else if (loadError) {
    const gone = loadError instanceof API.ApiError && loadError.status === 404;
    thread = (
      <EmptyState
        icon="messageQuestion"
        title={gone ? "Percakapan tidak ditemukan" : "Percakapan belum termuat"}
        body={gone ? "Percakapan ini sudah dihapus atau bukan milik Anda." : API.errorMessage(loadError)}
        action={
          <a className="btn btn-secondary" href={projectId ? `/project/${encodeURIComponent(projectId)}` : "/"}>
            Chat baru
          </a>
        }
      />
    );
  } else if (started) {
    const hello = greetingFor(conv?.id) ?? (!conv ? (greetingMessage?.text ?? null) : null);
    thread = (
      <>
      {hello ? (
        <ol className="thread chat-hello" aria-label="Sapaan Pluto">
          <PlutoSay>{hello}</PlutoSay>
        </ol>
      ) : null}
      <Thread
        messages={messages}
        pending={pending}
        unanswered={unanswered !== null}
        knowledge={knowledge}
        canDraft={canDraft}
        onRetry={retry}
        onAnswerAgain={answerAgain}
        conversationId={conv?.id ?? null}
        catalog={catalogState}
        onDocumentCreated={onDocumentCreated}
      />
      </>
    );
  }

  const composer = (
    <Composer
      value={text}
      onChange={setText}
      onSend={askText}
      busy={busy}
      placeholder={phone ? ASK_PLACEHOLDER_SHORT : ASK_PLACEHOLDER}
      attachments={active ? attachments : undefined}
      lead={started && faqItems.length > 0 && !hasSuggestions ? <FaqButton open={faqOpen} disabled={busy} onOpen={() => setFaqOpen(true)} /> : undefined}
    />
  );

  return (
    <>
      <ChatFrame
        started={started}
        title={title}
        scroller={scroller}
        thread={thread}
        composer={composer}
        greeting={greeting ?? (projectId && projectName?.trim() ? projectName.trim() : !projectId && user.role === "penyedia" ? PENYEDIA_GREETING : undefined)}
        greetingLevel={greetingLevel}
        aside={!started && !projectId && !greeting && user.role === "penyedia" ? <p className="chat-lead">{PENYEDIA_GREETING_LEAD}</p> : undefined}
        sub={user.role === "penyedia" ? PENYEDIA_GREETING_LEAD : undefined}
        hello={
          greetingMessage ? (
            <PlutoSay
              chips={
                greetingMessage.chips ??
                (hasSuggestions ? (
                  <SuggestionChips chips={suggestions!} disabled={busy} onPick={pickSuggestion} />
                ) : (
                  <FaqState state={faq} disabled={busy} onAsk={askFaq} draft={draftChip} />
                ))
              }
            >
              {greetingMessage.text}
            </PlutoSay>
          ) : undefined
        }
        keepHeading={greetingMessage?.keepHeading}
        dock={dock}
        chips={
          greetingMessage ? undefined : (intro ??
            (hasSuggestions ? (
              <SuggestionChips chips={suggestions!} disabled={busy} onPick={pickSuggestion} />
            ) : (
              <>
                <FaqState state={faq} disabled={busy} onAsk={askFaq} draft={draftChip} />
                {homePrompts ? <HomePrompts onAsk={askPrompt} disabled={busy} penyedia={user.role === "penyedia"} /> : null}
              </>
            )))
        }
        quota={
          hint || (started && hasSuggestions) ? (
            <>
              {hint ? (
                <p className="chat-hint" role="alert">
                  {hint}
                </p>
              ) : null}
              {started && hasSuggestions ? <SuggestionChips chips={suggestions!} disabled={busy} onPick={pickSuggestion} docked /> : null}
            </>
          ) : undefined
        }
      />
      <Dialog open={faqOpen} title="Pertanyaan umum" description="Jawaban tersimpan, tampil langsung." onClose={() => setFaqOpen(false)}>
        <FaqList items={faqItems} onAsk={askFaq} />
      </Dialog>
    </>
  );
}

/** The chips with their loading and error states. */
function FaqState({ state, disabled, onAsk, draft }: { state: Async<FaqItem[]>; disabled: boolean; onAsk: (item: FaqItem) => void; draft?: DraftChip }) {
  const shown = useDelayed(state.loading && !state.data);
  if (state.loading && !state.data) {
    return (
      <div className="chat-chips" role="status" aria-busy="true" aria-label="Memuat pertanyaan umum">
        {shown ? Array.from({ length: 3 }, (_, i) => <span key={i} className="sk chat-chip-sk" aria-hidden="true" />) : null}
      </div>
    );
  }
  if (state.error) {
    return (
      <Notice
        tone="danger"
        title="Pertanyaan umum belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={state.reload}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(state.error)} Anda tetap dapat mengetik pertanyaan di atas.</p>
      </Notice>
    );
  }
  return <FaqChips items={state.data ?? []} disabled={disabled} onAsk={onAsk} draft={draft} />;
}

/** The saved questions as rows (the Pertanyaan umum dialog in a thread). */
function FaqList({ items, onAsk }: { items: FaqItem[]; onAsk: (item: FaqItem) => void }) {
  if (items.length === 0) return null;
  return (
    <section className="faq" aria-label="Pertanyaan umum">
      <ul className="faq-list">
        {items.map((item) => (
          <li key={item.id}>
            <button type="button" className="faq-row" onClick={() => onAsk(item)}>
              <span className="faq-tile" aria-hidden="true">
                <Glyph name={faqGlyph(item.icon)} />
              </span>
              <span className="faq-text">
                <span className="faq-q">{item.question}</span>
                <span className="faq-short">{item.short}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ConversationLoading() {
  const shown = useDelayed(true);
  return (
    <div className="chat-loading" role="status" aria-busy="true" aria-label="Memuat percakapan">
      {shown ? (
        <>
          <span className="sk chat-sk-q" />
          <span className="sk chat-sk-a" />
          <span className="sk chat-sk-b" />
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Guest chat: memory only, POST /chat/guest, 5 messages a day
// ---------------------------------------------------------------------------
let guestSeq = 0;

function guestMessage(role: "user" | "assistant", content: string, citations: string[] = [], attachments: AttachmentRef[] = []): ThreadMessage {
  guestSeq += 1;
  return {
    id: `guest-${guestSeq}`,
    role,
    content,
    source: role === "user" ? "user" : "ai",
    faqId: null,
    citations,
    createdAt: new Date().toISOString(),
    kind: "text",
    proposal: null,
    documentId: null,
    attachments,
    document: null,
  } as ThreadMessage;
}

/** The guest's inline file, kept for a retry. */
interface GuestFile {
  name: string;
  data: string;
  /** For the chip in the question (never stored, so no id). */
  ref: AttachmentRef;
}

/** The wait after a round of guest messages (the API's GUEST_COOLDOWN_SECONDS, which decides). */
export const GUEST_COOLDOWN_MINUTES = 30;

/** True for the API's 429 { code: "guest_cooldown" | "guest_limit" } (or any guest 429). */
export function isGuestLimit(err: unknown): boolean {
  return err instanceof API.ApiError && err.status === 429;
}

/** Seconds to wait from a guest 429: the Retry-After header, else the body's retryAfter, else one cooldown. */
export function guestRetryAfter(err: API.ApiError): number {
  const fromBody = err.body?.retryAfter;
  const seconds = err.retryAfter ?? (typeof fromBody === "number" ? fromBody : null);
  return seconds !== null && seconds > 0 ? seconds : GUEST_COOLDOWN_MINUTES * 60;
}

/** "kurang dari 1 menit", "29 menit", "3 jam 20 menit" (rounded up to the minute). */
export function waitText(ms: number): string {
  if (ms < 60_000) return "kurang dari 1 menit";
  return formatMinutes(Math.ceil(ms / 60_000)) ?? "beberapa menit";
}

/** The quota line under the composer: what is known, never invented. */
export function quotaText(remaining: number | null, limit: number): string {
  if (remaining === null) return `Tanpa masuk, Anda dapat mengirim ${limit} pesan, lalu jeda ${GUEST_COOLDOWN_MINUTES} menit.`;
  return `Sisa ${remaining} dari ${limit} pesan sebelum jeda`;
}

// ---------------------------------------------------------------------------
// Guest chat. A guest lands straight in the chat (no introduction) and is
// greeted as GUEST_NAME, which also goes with every POST /chat/guest as the
// profile name (tone only). Conversations stay on this device for 24 hours
// (chat/guest-store.ts); /?tamu=<id> opens one from the sidebar.
// ---------------------------------------------------------------------------

/** What Pluto calls every guest. */
export const GUEST_NAME = "Teman Pluto";

function toThreadMessage(conv: GuestConversation, turn: GuestTurn, i: number): ThreadMessage {
  const files: AttachmentRef[] = (turn.files ?? []).map((f) => ({ id: "", name: f.name, mimeType: f.mimeType, size: f.size, kind: "upload" }));
  return { ...guestMessage(turn.role, turn.content, turn.citations, files), id: `${conv.id}-${i}`, createdAt: turn.createdAt } as ThreadMessage;
}

function GuestChat() {
  useTitle("Chat");
  const loc = useLocation();
  const openId = loc.query.get("tamu");
  const guest = useGuestState();
  const conversation = openId ? (guest.conversations.find((c) => c.id === openId) ?? null) : null;
  const gone = openId !== null && conversation === null;
  const [pending, setPending] = useState<Pending | null>(null);
  const [text, setText] = useState("");
  const [remaining, setRemaining] = useState<number | null>(null);
  const [limit, setLimit] = useState(GUEST_LIMIT);
  const [faqOpen, setFaqOpen] = useState(false);
  const { scroller, mode: scrollMode } = useThreadScroll();
  const faq = useAsync(() => API.guestFaq() as Promise<FaqItem[]>, []);
  const attachments = useAttachments({ guest: true });
  const phone = useMedia(PHONE);
  const retryFile = useRef<GuestFile | null>(null);

  // Expired guest data leaves the device on load and every minute.
  useEffect(() => startGuestPurge(), []);
  // While the guest waits, the countdown ticks and the composer opens again
  // right when the cooldown ends (the store then drops it).
  const until = guest.cooldownUntil;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (until === null) {
      setRemaining((r) => (r === 0 ? null : r));
      return;
    }
    const tick = setInterval(() => setTick((t) => t + 1), 15_000);
    const end = setTimeout(refreshGuest, Math.max(0, until - guestNow()) + 50);
    return () => {
      clearInterval(tick);
      clearTimeout(end);
    };
  }, [until]);
  // Another conversation (or Chat baru) starts without a pending turn.
  useEffect(() => {
    setPending(null);
    scrollMode.current = "bottom";
  }, [openId, scrollMode]);

  const messages: ThreadMessage[] = conversation ? conversation.messages.map((t, i) => toThreadMessage(conversation, t, i)) : [];
  const waitMs = until === null ? 0 : until - guestNow();
  const locked = waitMs > 0;
  const busy = pending?.status === "sending";
  const started = messages.length > 0 || pending !== null;

  const send = async (message: string, file: GuestFile | null = null) => {
    if (busy || locked) return;
    scrollMode.current = "bottom";
    const files = file ? [file.ref] : [];
    retryFile.current = file;
    setPending({ status: "sending", input: { message }, text: message, bubble: true, files });
    try {
      const history = (conversation?.messages ?? []).slice(-GUEST_HISTORY).map((m) => ({ role: m.role, content: m.content }));
      const guestProfile = { name: GUEST_NAME };
      const reply = await API.guestChat(file ? { message, history, file: { name: file.name, data: file.data }, profile: guestProfile } : { message, history, profile: guestProfile });
      scrollMode.current = "question";
      retryFile.current = null;
      const at = new Date(guestNow()).toISOString();
      const base: GuestConversation = conversation ?? { id: newGuestConversationId(), title: shortenTitle(message), createdAt: guestNow(), updatedAt: guestNow(), messages: [] };
      saveGuestConversation({
        ...base,
        updatedAt: guestNow(),
        messages: [...base.messages, { role: "user", content: message, citations: [], createdAt: at, files: files.map((f) => ({ name: f.name, mimeType: f.mimeType, size: f.size })) }, { role: "assistant", content: reply.message.content, citations: reply.message.citations, createdAt: at }],
      });
      setRemaining(reply.remaining);
      // The round's last message: the API's cooldown (or the address day) starts now.
      if (reply.remaining === 0) setGuestCooldown(guestNow() + (reply.retryAfter ?? GUEST_COOLDOWN_MINUTES * 60) * 1000);
      setLimit(reply.limit);
      setPending(null);
      if (!conversation) navigate(`/?tamu=${encodeURIComponent(base.id)}`, { replace: true });
    } catch (err) {
      if (isGuestLimit(err)) {
        setGuestCooldown(guestNow() + guestRetryAfter(err as API.ApiError) * 1000);
        setRemaining(0);
        setPending(null);
        return;
      }
      const error = err instanceof API.ApiError ? err : new API.ApiError(0, API.errorMessage(err));
      scrollMode.current = "bottom";
      setPending({ status: "error", input: { message }, text: message, bubble: true, error, files });
    }
  };

  const askText = (typed: string) => {
    const it = attachments.ready[0];
    const file: GuestFile | null = it?.data ? { name: it.name, data: it.data, ref: { id: "", name: it.name, mimeType: it.mimeType, size: it.size, kind: "upload" } } : null;
    setText("");
    attachments.clear();
    void send(typed || FILES_ONLY_MESSAGE, file);
  };
  const askFaq = (item: FaqItem) => {
    setFaqOpen(false);
    void send(item.question);
  };
  const retry = () => {
    if (pending?.status === "error") void send(pending.text, retryFile.current);
  };

  const faqItems = faq.data ?? [];
  const composer = (
    <Composer
      value={text}
      onChange={setText}
      onSend={askText}
      busy={busy}
      disabled={locked}
      attachments={locked ? undefined : attachments}
      placeholder={locked ? "Tunggu jeda selesai untuk mengirim pesan lagi" : phone ? ASK_PLACEHOLDER_SHORT : ASK_PLACEHOLDER}
      lead={started && faqItems.length > 0 && !locked ? <FaqButton open={faqOpen} disabled={busy} onOpen={() => setFaqOpen(true)} /> : undefined}
    />
  );

  const quota = locked ? (
    <div className="chat-lock">
      {/* A wait past one cooldown is the address's daily cap. */}
      <Notice tone="info" title={waitMs > GUEST_COOLDOWN_MINUTES * 60_000 ? "Batas pesan tamu hari ini tercapai" : `Batas ${limit} pesan tamu tercapai`}>
        <p>
          Anda dapat mengirim pesan lagi dalam {waitText(waitMs)}. Masuk dengan Google untuk melanjutkan sekarang, menyimpan riwayat, dan membuat project.
        </p>
      </Notice>
      <GoogleButton block />
    </div>
  ) : (
    <p className="chat-quota" role="status">
      {quotaText(remaining, limit)}
    </p>
  );

  const aside = gone ? (
    <Notice tone="info" title="Percakapan tamu ini sudah terhapus" className="guest-gone">
      <p>Riwayat tamu terhapus otomatis setelah 24 jam. Mulai percakapan baru di bawah.</p>
    </Notice>
  ) : null;

  return (
    <>
      <ChatFrame
        started={started}
        title={conversation?.title || "Percakapan tamu"}
        scroller={scroller}
        greeting={`Halo, ${GUEST_NAME}. Mau tanya apa hari ini?`}
        aside={aside}
        thread={
          <Thread
            messages={messages}
            pending={pending}
            unanswered={false}
            knowledge={null}
            canDraft={false}
            onRetry={retry}
            onAnswerAgain={() => undefined}
            guest
          />
        }
        composer={composer}
        chips={locked ? null : <FaqState state={faq} disabled={busy} onAsk={askFaq} />}
        quota={quota}
      />
      <Dialog open={faqOpen} title="Pertanyaan umum" description="Pilih pertanyaan untuk dikirim." onClose={() => setFaqOpen(false)}>
        <FaqList items={faqItems} onAsk={askFaq} />
      </Dialog>
    </>
  );
}
